import { useState, useCallback, useRef } from "react";
import axios, { CancelTokenSource } from "axios";
import { API_CONFIG } from "../config/apiConfig";
import { Header } from "../types";
import { apiClient } from "../utils/apiClient";

interface ApiResponse {
  success: boolean;
  data?: any[];
  error?: string;
  statusCode?: number;
  body?: string;
}

interface HeaderItem {
  returnHeaderId?: number;
  fromOrgName: string;
  toOrgName: string;
  type: string;
  status: string;
  creationDate: string;
  trackingNumber: string;
  trackingStatus: string;
  expectedDeliveryDate: string;
  deliveredDate: string;
  region: string;
  toOrg: string;
  TO_ORG?: string;
}

export const useSearchHeaders = () => {
  const [headers, setHeaders] = useState<Header[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const mapHeaderData = useCallback(
    (item: HeaderItem): Header => ({
      requestNumber: item.returnHeaderId?.toString() || "",
      fromSite: item.fromOrgName,
      toSite: item.toOrgName,
      type: item.type,
      status: item.status,
      creationDate: item.creationDate,
      trackingNumber: item.trackingNumber,
      trackingStatus: item.trackingStatus,
      expectedDeliveryDate: item.expectedDeliveryDate,
      deliveredDate: item.deliveredDate,
      region: item.region,
      toOrg: item.toOrg || item.TO_ORG || "",
    }),
    []
  );

  const parseResponseData = useCallback((data: any): ApiResponse => {
    if (data.data?.statusCode) {
      if (data.data.statusCode === 500) {
        const errorBody = JSON.parse(data.data.body);
        throw new Error(errorBody.error || "Database error");
      }
      return JSON.parse(data.data.body);
    }
    return data;
  }, []);

  const searchHeaders = useCallback(
    async (searchString: string, includeClosedOrders: boolean = false, pageNumber: number = 1, pageSize: number = 10) => {
      const trimmedSearch = searchString.trim();
      if (!trimmedSearch) {
        setHeaders([]);
        return;
      }

      // Cancel previous request
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("New request initiated");
      }
      cancelTokenRef.current = axios.CancelToken.source();

      setLoading(true);
      setError(null);
      if (pageNumber === 1) {
        setHeaders([]);
        setCurrentPage(1);
        setHasMore(false);
      }

      try {
        const { data } = await apiClient.get<ApiResponse>(
          API_CONFIG.ENDPOINTS.SEARCH_HEADERS,
          {
            params: {
              search: String(trimmedSearch || '').trim(),
              include_closed_orders: includeClosedOrders ? 'Y' : 'N',
              page_number: pageNumber,
              page_size: pageSize
            },
            cancelToken: cancelTokenRef.current.token,
          }
        );

        const responseData = parseResponseData(data);

        if (responseData.success && Array.isArray(responseData.data)) {
          const mappedHeaders = responseData.data.map(mapHeaderData);
          if (pageNumber === 1) {
            setHeaders(mappedHeaders);
          } else {
            setHeaders(prev => [...prev, ...mappedHeaders]);
          }
          setCurrentPage(pageNumber);
          // Always assume there are more records unless we get less than pageSize
          setHasMore(mappedHeaders.length >= pageSize);
        } else {
          throw new Error(responseData.error || "Failed to fetch headers");
        }
      } catch (err: any) {
        if (!axios.isCancel(err)) {
          const errorMessage =
            err.response?.data?.error ||
            err.response?.data?.message ||
            err.message;
          setError(errorMessage);
          setHeaders([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [mapHeaderData, parseResponseData]
  );

  const loadMoreHeaders = useCallback(
    async (searchString: string, includeClosedOrders: boolean = false) => {
      if (!hasMore || loading) return;
      await searchHeaders(searchString, includeClosedOrders, currentPage + 1, 10);
    },
    [searchHeaders, currentPage, hasMore, loading]
  );

  return {
    headers,
    loading,
    error,
    searchHeaders,
    loadMoreHeaders,
    hasMore,
    currentPage
  };
};

// import { useState, useCallback } from 'react';
// import axios, { CancelTokenSource } from 'axios';
// import { API_CONFIG } from '../config/apiConfig';
// import { Header } from '../types';

// let cancelTokenSource: CancelTokenSource | null = null;

// export const useSearchHeaders = () => {
//   const [headers, setHeaders] = useState<Header[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);

//   const searchHeaders = useCallback(async (searchString: string) => {
//     if (!searchString.trim()) {
//       setHeaders([]);
//       return;
//     }

//     // Cancel previous request
//     if (cancelTokenSource) {
//       cancelTokenSource.cancel('New request initiated');
//     }
//     cancelTokenSource = axios.CancelToken.source();

//     setLoading(true);
//     setError(null);
//     setHeaders([]);

//     const startTime = performance.now();

//     try {
//       const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SEARCH_HEADERS}`;

//       const response = await axios.get(url, {
//         params: { search: searchString },
//         timeout: API_CONFIG.TIMEOUT,
//         cancelToken: cancelTokenSource.token,
//         headers: {
//           'Content-Type': 'application/json',
//         }
//       });

//       const data = response.data;
//       const endTime = performance.now();
//       console.log(`API Response received in ${(endTime - startTime).toFixed(2)}ms:`, data);

//       // Handle nested Lambda response
//       let responseData = data;
//       if (data.data && data.data.statusCode) {
//         if (data.data.statusCode === 500) {
//           const errorBody = JSON.parse(data.data.body);
//           throw new Error(errorBody.error || 'Database error');
//         }
//         responseData = JSON.parse(data.data.body);
//       }

//       if (responseData.success && responseData.data) {
//         if (Array.isArray(responseData.data)) {
//           const headers = responseData.data.map((item: any) => ({
//             requestNumber: item.returnHeaderId?.toString(),
//             fromSite: item.fromOrgName,
//             toSite: item.toOrgName,
//             type: item.type,
//             status: item.status,
//             creationDate: item.creationDate,
//             trackingNumber: item.trackingNumber,
//             trackingStatus: item.trackingStatus,
//             expectedDeliveryDate: item.expectedDeliveryDate,
//             deliveredDate: item.deliveredDate,
//             region: item.region,
//             toOrg: item.toOrg || item.TO_ORG
//           }));
//           setHeaders(headers);
//         } else {
//           console.error('responseData.data is not an array:', responseData.data);
//           setHeaders([]);
//         }
//       } else {
//         throw new Error(responseData.error || 'Failed to fetch headers');
//       }
//     } catch (err: any) {
//       if (!axios.isCancel(err)) {
//         const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;
//         setError(errorMessage);
//         setHeaders([]);
//       }
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   return {
//     headers,
//     loading,
//     error,
//     searchHeaders
//   };
// };
